﻿/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* tslint:disable */
import { ActivityContextUpdateHandler, Callback } from "../contracts/contracts";
import { ActivityStatus } from "../contracts/activityStatus";
import {
  ActivityContextChangedEventContext,
  ActivityStatusChangeEventContext,
  EntityEvent,
  EntityEventContext,
  EntityEventType
} from "../contracts/entityEvent";

import ActivityType from "../entities/activityType";
import WindowType from "../entities/windowType";
import Activity from "../entities/activity";
import ActivityEntity from "../entities/activityEntity";
import ActivityWindow from "../entities/activityWindow";
import WindowSource from "../entities/windowSource";

import { nodeify } from "../helpers/promiseExtensions";
import { ReadyMarker } from "../helpers/readyMarker";
import { EntityObservableCollection } from "../helpers/entityObservableCollection";
import { Logger } from "../helpers/logger";
import * as util from "../helpers/util";

import { ActivityBridge } from "../bridges/activityBridge";
import { WindowDefinition } from "../entities/windowDefinition";
import { WindowBounds } from "../contracts/windowBounds";
import { CloneOptions } from "../entities/cloneOptions";
import { AttachedActivityDescriptor } from "../entities/attachedActivityDescriptor";
import { ActivityDescriptor } from "../entities/activityDescriptor";
import { ActivityState } from "../contracts/entityEvent";
import { WindowFactory } from "../contracts/windowFactory";
import { Glue42 } from "../../../glue";
/* tslint:enable */

type OverrideTypeDefinition = Glue42.Activities.OverrideTypeDefinition;

export default class ActivityManager {
  private _bridge: ActivityBridge;
  private _readyMarker: ReadyMarker;
  private _dataReadyMarker: ReadyMarker;
  private _descriptorsMarker: ReadyMarker;

  private _activityTypes: EntityObservableCollection<ActivityType>;
  private _windowTypes: EntityObservableCollection<WindowType>;
  private _activities: EntityObservableCollection<Activity>;
  private _windows: EntityObservableCollection<ActivityWindow>;
  private _logger: Logger = Logger.Get("activityManager");
  private _announcedWindows: ActivityWindow[] = [];
  private _attachedCallbacks: ((newAct: any, descriptor: any) => void)[] = [];
  private _detachedCallbacks: ((newAct: any, oldAct: Activity, descriptor: any) => void)[] = [];
  private _frameColorChangesCallbacks: ((act: Activity, frameColor: string) => void)[] = [];
  private _windowHandlers: any[][] = [];

  public get usingHc(): boolean {
    return this._bridge.bridgeType === "HC";
  }

  public get announcedWindows(): ActivityWindow[] {
    return this._announcedWindows;
  }

  public set announcedWindows(v: ActivityWindow[]) {
    throw new Error("not allowed");
  }

  constructor(bridge: ActivityBridge, autoAnnounce: boolean, windows: any) {
    this._bridge = bridge;
    this._activityTypes = new EntityObservableCollection<ActivityType>((e) => this._grabEntity(e));
    this._windowTypes = new EntityObservableCollection<WindowType>((e) => this._grabEntity(e));
    this._activities = new EntityObservableCollection<Activity>((e) => this._grabEntity(e));
    this._windows = new EntityObservableCollection<ActivityWindow>((e) => this._grabEntity(e));

    // signals to wait for - get AT, get WT, get Activities and get Windows
    this._dataReadyMarker = new ReadyMarker("Activity Manager Data", ["GetActivityTypes", "GetWindowTypes", "GetActivities", "GetWindows"].length);
    this._descriptorsMarker = new ReadyMarker("Attached Activities Descriptors", ["GetDescriptors"].length);

    // waiting for activity data first

    // NB: in the GW3 case, we don't have the ability to request data at will
    // but need wait for delta messages
    // So GW3Bridge returns empty arrays from the get() calls
    // We're relying on the fact that the GW3 bridge will wait
    // until resolving its .ready()

    if (autoAnnounce) {
      // if auto announce replace the ready marker, so:
      // 1) wait for data
      // 2) get descriptors
      // 3) announce the current window

      this._readyMarker = new ReadyMarker("Activity Manager Announce", ["Announcement"].length);
      this._dataReadyMarker.setCallback((dataErr: any) => {
        if (dataErr) {
          this._readyMarker.error(dataErr);
        }

        this._descriptorsMarker.setCallback((err: any) => {

          if (err) {
            this._readyMarker.error(err);
          }

          this._logger.debug("Auto announcing window");
          // signal no matter if it failed or not
          this.announceWindow()
            .then((w) => {
              // NB: this creates glue.activities.my.window
              this._announcedWindows.push(w);
              this._readyMarker.signal("Successfully announced window with id '" + w.id + "'");
            })
            .catch((errCatch) => {
              this._logger.debug("Will not announce window - " + errCatch);
              this._readyMarker.signal();
            });
        });

        this.refreshDescriptors();
      });
    } else {
      // TODO: not sure if this still works
      this._readyMarker = this._dataReadyMarker;
    }

    this._bridge.onActivitiesAttached((e) => {
      this._handleActivitiesAttached(e);
    });
    this._bridge.onActivitiesDetached((e) => {
      this._handleActivitiesDetached(e);
    });
    this._bridge.onActivityAttachedDescriptorsRefreshed((e) => {
      this._handleActivityDescriptorsRefreshed(e);
    });
    if (windows) {
      windows.onWindowFrameColorChanged(this._handleWindowFrameColorChanged.bind(this));
    }

    // initialize the bridge
    this._bridge.init();

    // NB: there is no race condition here.
    // Since browser/Node JS is a single-threaded environment, assuming no events on _bridge
    // fire inside .init(), this subscribe will catch anything that comes between it and the init() call
    this._subscribeForData();

    // NB: in GW3 case, the initial state messages after a JOIN come in before the JOIN's SUCCESS
    // message (see https://confluence.tick42.com:8443/display/TD/Global+Gateway+Domain#GlobalGatewayDomain-JOIN)
    // so the .initReady().then() callback will only get invoked after we've received the initial domain
    // state. We need to be ready to handle events before that, hence the _subscribeForData() above.
    // what's more, our own JOINED ACTIVITY will come only after we've sent our READY, hence waiting
    // for bridge.ready() in the manager's ready()

    // wait for it to become ready and then start getting/subscribing for data
    this._bridge
      .initReady()
      .then((aw) => {
        this._getInitialData();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  public ready(callback?: (err: string, manager: ActivityManager) => void): Promise<ActivityManager> {
    const promise = new Promise<ActivityManager>((resolve, reject) => {
      this._readyMarker.setCallback(
        (err) => {
          if (!err) {
            resolve(this);
          } else {
            reject(this._readyMarker.getError());
          }
        });
    });

    return nodeify(Promise.all([this._bridge.ready(), promise]).then(() => this), callback);
  }

  /**
   * @returns All known activity types
   */
  public getActivityTypes(): ActivityType[] {
    return this._activityTypes.get();
  }

  /**
   * Gets ActivityType by name
   * @param name  Name to search for
   * @returns     If found the @type ActivityType; otherwise undefined
   */
  public getActivityType(name: string): ActivityType {
    return this._activityTypes.getByName(name);
  }

  /**
   * Registers a new activity type
   * @param activityTypeName      The name of the activity type to be created
   * @param ownerWindowType       The type of the owner window or a WindowDefinition for the owner window
   * @param helperWindowTypes     Types of helper windows (or WindowDefinitions for helper windows) for that activity type
   * @param config                (HC only, deprecated) Layout config
   * @param description           (HC only, deprecated) Description for the new activity type
   * @param callback              (Optional) Callback for results - if not specified the method will return a promise
   */
  public registerActivityType(
    activityTypeName: string,
    ownerWindowType: string | WindowDefinition,
    helperWindowTypes?: string[] | WindowDefinition[],
    config?: any,
    description?: string,
    callback?: Callback<ActivityType>): Promise<ActivityType> {
    const promise = new Promise<ActivityType>((resolve, reject) => {

      // activityTypeName checks
      if (util.isUndefinedOrNull(activityTypeName)) {
        reject("activityTypeName argument can not be undefined");
        return;
      }
      if (!util.isString(activityTypeName)) {
        reject("activityTypeName should be string");
        return;
      }
      // check if type is already defined
      const actType = this.getActivityType(activityTypeName);
      if (!util.isUndefinedOrNull(actType)) {
        reject("Activity type '" + activityTypeName + "' already exists");
        return;
      }

      // ownerWindowType checks
      let ownerDefinition: WindowDefinition;
      if (util.isUndefined(ownerWindowType)) {
        reject("Owner window type can not be undefined");
        return;
      }
      if (util.isString(ownerWindowType)) {
        ownerDefinition = { type: (ownerWindowType as string), name: "", isIndependent: false, arguments: {} };
      } else {
        ownerDefinition = (ownerWindowType as WindowDefinition);
      }

      // helperWindowTypes checks
      const helperDefinitions: WindowDefinition[] = [];
      if (!util.isUndefined(helperWindowTypes) && util.isArray(helperWindowTypes)) {
        // eslint-disable-next-line @typescript-eslint/no-for-in-array, guard-for-in
        for (const index in helperWindowTypes) {
          const item = helperWindowTypes[index];
          if (util.isString(item)) {
            const definition = {
              type: (item as string),
              name: "",
              isIndependent: false,
              arguments: {},
              relativeTo: "",
              relativeDirection: "",
              windowStyleAttributes: {}
            };
            helperDefinitions.push(definition);
          } else {
            helperDefinitions.push(item as WindowDefinition);
          }
        }
      }

      // redirect to bridge
      this._bridge
        .registerActivityType(activityTypeName,
          ownerDefinition,
          helperDefinitions,
          config,
          description)
        .then((activityType) => {
          // add to from collections
          this._grabEntity(activityType);
          resolve(activityType);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return nodeify(promise, callback);
  }

  /**
   * Unregister existing activity type
   * @param type The name of the activity type to be removed
   */
  public unregisterActivityType(type: string, callback?: Callback<any>) {
    const promise = new Promise<ActivityType>((resolve, reject) => {
      // check if type is defined
      const actType = this.getActivityType(type);
      if (util.isUndefined(actType)) {
        reject("Activity type '" + type + "' does not exists");
        return;
      }
      this._bridge.unregisterActivityType(type).then(() => resolve(actType), reject);
    });
    return nodeify(promise, callback);
  }

  /**
   * Initiates a new activity from the given type.
   * @param activityType  The ActivityType to initiate
   * @param context       The initial context of the activity
   * @param callback      Callback for result
   * @param configuration Optional configuration passed to window/peer factories for the new activity's participants
   * @returns             Promise for activity
   */
  public initiate(activityType: string, context?: object, callback?: Callback<Activity>, configuration?: WindowDefinition[] | OverrideTypeDefinition): Promise<Activity> {
    const promise = new Promise<Activity>((resolve, reject) => {
      // check if type is defined
      const actType = this.getActivityType(activityType);
      if (util.isUndefined(actType)) {
        reject("Activity type '" + activityType + "' does not exists");
        return;
      }

      this._bridge
        .initiateActivity(activityType, context, configuration)
        .then((actId) => {

          this._activities
            .getOrWait(actId)
            .then((act: Activity) => {
              resolve(act);
            })
            .catch((err) => reject(err));
        })
        .catch((err) => {
          reject(err);
        });
    });
    return nodeify(promise, callback);
  }

  public subscribeActivityTypeEvents(handler: (act: ActivityType, action: string) => void) {
    this._activityTypes.subscribe((at, context) => {
      handler(at, context.type);
    });
  }

  /**
   * @returns All known WindowTypes
   */
  public getWindowTypes(): WindowType[] {
    return this._windowTypes.get();
  }

  /**
   * Gets WindowType by name
   * @param name  The name of the type @type WindowType
   * @returns     WindowType if found; undefined otherwise
   */
  public getWindowType(name: string): WindowType {
    return this._windowTypes.getByName(name);
  }

  /**
   * Registers a factory for a given WindowType. The factory will be called once window of that type is requested
   * @param windowType   Window type that will be constructed from the factory. Can be a string (name of the window type)
   * or object that has name, description properties
   * @param factoryMethod      The factory that will construct Windows of that type
   * @param description
   * @param callback
   * @param configuration
   * @returns {}
   */
  public registerWindowFactory(
    windowType: any,
    factoryMethod: WindowFactory,
    callback?: any): Promise<any> {
    const promise = new Promise<any>((resolve, reject) => {
      if (util.isUndefinedOrNull(windowType)) {
        reject("no windowType specified");
        return;
      }

      if (util.isObject(windowType)) {
        windowType = windowType.getName();
      } else if (!util.isString(windowType)) {
        reject("windowType should be string or object that has getName method");
        return;
      }

      this._bridge
        .registerWindowFactory(windowType, factoryMethod)
        .then((v) => {
          resolve(v);
        })
        .catch((err) => {
          reject(err);
        });
    });
    return nodeify(promise, callback);
  }

  /**
   * Unregisters all factories for a given WindowType.
   * @param windowType   Window type that is constructed from the factory.
   * @param callback
   * @returns {}
   */
  public unregisterWindowFactory(windowType: string, callback?: any): Promise<any> {
    const promise = new Promise<any>((resolve, reject) => {
      if (util.isUndefinedOrNull(windowType)) {
        reject("no windowType specified");
        return;
      }

      if (!util.isString(windowType)) {
        reject("windowType should be a string");
        return;
      }

      this._bridge
        .unregisterWindowFactory(windowType)
        .then((v) => {
          resolve(v);
        })
        .catch((err) => {
          reject(err);
        });
    });
    return nodeify(promise, callback);
  }

  /**
   * @returns All started activities
   * @param activityType Can be a string or @type ActivityType. If string a new activityType will be created using the string for name.
   * @returns Array of Activity objects
   */
  public getActivities(activityType?: string | ActivityType | string[] | ActivityType[]): Activity[] {
    let act = this._activities.get();
    act = act.filter((a) => a._ownerId);
    if (!activityType) {
      return act;
    }
    let types: any = activityType;
    if (util.isString(activityType)) {
      types = [activityType];
    } else if (activityType instanceof ActivityType) {
      types = [activityType.name];
    } else if (activityType instanceof Array) {
      // TODO check if string array or array of ActivityTypes
    } else {
      throw new Error("Invalid input argument 'activityType' = " + activityType);
    }

    return act.filter((at: Activity) => {
      const type = at.type;
      return util.some(types, (t: ActivityType) => {
        return type.id === t.id;
      });
    });
  }

  /**
   * Returns Activity by ID
   */
  public getActivityById(id: string): Activity {
    return this._activities.getByName(id);
  }

  /**
   * Initialises activity for the current window - by doing this the window is announced as activity aware to the other participants
   * @param windowType            Type of window
   * @param activityWindowId      The id of the window that was created
   * @returns {}                  Promise for an activity window (joined to an activity, if the activity creation fails the promise will be rejected)
   */
  public announceWindow(activityWindowId?: string, windowType?: string): Promise<ActivityWindow> {
    const promise = new Promise<ActivityWindow>((resolve, reject) => {

      // get the announcement info from the bridge (e.g. the context in HC) - this will be used if no arguments passed
      const announcementInfo = this._bridge.getAnnouncementInfo();
      if (util.isUndefined(activityWindowId)) {
        activityWindowId = announcementInfo.activityWindowId;
      }
      if (util.isUndefined(windowType)) {
        windowType = announcementInfo.activityWindowType;
      }
      if (util.isUndefinedOrNull(windowType)) {
        throw new Error("Can not announce - unknown windowType");
      }

      const activityId = announcementInfo && announcementInfo.activityId;

      // We cover two cases here:
      // * no activityWindowId supplied (neither in arguments or announcement info) - in that case we register new window by type. Most of the time this is the case when an independent window is created outside activity
      // * activityWindowId supplied - window created as part of activity creation

      // NB: in GW3, windows are created standalone, and receive 'JOINED' after they send a 'READY'
      // so we always go in 'if' case, not 'else'
      if (util.isUndefinedOrNull(activityWindowId)) {
        this._logger.debug("Registering window with type:'" + windowType + "', name:'" + announcementInfo.activityWindowName + "', ind.:'" + announcementInfo.activityWindowIndependent + "'");
        // register new window created outside activity
        this._bridge.registerWindow(windowType, announcementInfo.activityWindowName, announcementInfo.activityWindowIndependent)
          .then(this._windows.getOrWait.bind(this._windows))
          .then((w: any) => {
            if (activityId) {
              return this._activities.getOrWait(activityId).then((_: any) => w);
            } else {
              return w;
            }
          })
          .then((w: any) => {
            resolve(w);
          })
          .catch((err) => {
            this._logger.error(err);
          });

      } else {
        // announce new window created in an activity creation process
        this._logger.debug("Announcing window with id '" + activityWindowId + "' and type '" + windowType + "'");

        // check if the window is already created -
        const currentWindow = this._windows.getByName(activityWindowId);
        if (!util.isUndefinedOrNull(currentWindow)) {
          this._logger.debug("Window with id '" + activityWindowId + "' already announced - reusing the window");
          resolve(currentWindow);
          return;
        }

        // window events handler
        const windowEventHandler = (a: Activity, w: ActivityWindow, e: any) => {
          if (activityWindowId === w.id) {
            if (e === EntityEventType.ActivityWindowJoinedActivity) {
              const activity = w.activity;
              if (util.isUndefined(activity)) {
                reject("UNDEFINED ACTIVITY");
              }
              this._logger.trace("Got joined event for id '" + activityWindowId + "'");
              resolve(w);
              this.unsubscribeWindowEvents(windowEventHandler);
            }
          }
        };

        this.subscribeWindowEvents(windowEventHandler);
        this._logger.trace("Waiting for joined event for id '" + activityWindowId + "'");
        this._bridge.announceWindow(windowType, activityWindowId);
      }
    });
    return promise;
  }

  /**
   * Allows you to subscribe for windows events, e.g. window type registered
   * @param handler   The handler to receive notifications
   * @returns {}
   */
  public subscribeWindowTypeEvents(handler: (act: WindowType, event: string) => void) {
    this._windowTypes.subscribe((wt, context) => {
      handler(wt, context.type);
    });
  }

  /**
   * Subscribe for activity status events
   * @param handler Handler function that will receive status notifications
   * @returns {}
   */
  public subscribeActivityEvents(handler: (activity: Activity, newStatus: ActivityStatus, oldStatus: ActivityStatus) => void): () => void {
    return this._activities.subscribe((act, context) => {
      if (context.type === EntityEventType.StatusChange) {
        const p = (context as ActivityStatusChangeEventContext);
        handler(act, p.newStatus, p.oldStatus);
      }

      // implicitly leave the activity on destroy
      if (context.type === EntityEventType.Removed ||
        (context.type === EntityEventType.StatusChange &&
          (context as ActivityStatusChangeEventContext).newStatus.getState() === ActivityState.Destroyed)) {
        for (const window of this._windows.get()) {
          if (window.activity && window.activity.id === act.id) {
            this._windows.process(new EntityEvent<ActivityWindow>(window, new EntityEventContext(EntityEventType.ActivityWindowLeftActivity)));
          }
        }
      }
    });
  }

  /**
   * Allows you to subscribe for windows events, e.g. window joining to activity
   * @param handler   The handler to receive notifications
   * @returns {}
   */
  public subscribeWindowEvents(handler: (activity: Activity, window: ActivityWindow, event: any) => void): () => void {
    const wrappingHandler = (window: ActivityWindow, context: any) => {
      let eventType = context.type;
      if (eventType === EntityEventType.Added) {
        // rename added to opened (because added is misleading - semantically it's the opposite of remove);
        eventType = "opened";
      }
      handler(window.activity, window, eventType);
    };
    this._windowHandlers.push([handler, wrappingHandler]);
    return this._windows.subscribe(wrappingHandler);
  }

  public unsubscribeWindowEvents(handler: (activity: Activity, window: ActivityWindow, event: any) => void) {
    const found = this._windowHandlers.find((pair) => pair[0] === handler);
    if (found) {
      this._windowHandlers.splice(this._windowHandlers.indexOf(found), 1);
      this._windows.unsubscribe(found[1]);
    }
  }

  /**
   * Creates a new window from a given type and joins it to an activity
   *
   * @param activity      Activity to join the window to
   * @param windowType    The window type to join (string or window definition)
   * @param callback      (Optional) Result callback
   * @returns             Promise for ActivityWindow
   */
  public createWindow(activity: Activity, windowTypeOrConfiguration: string | WindowDefinition | any, callback?: Callback<ActivityWindow>): Promise<ActivityWindow> {
    const promise = new Promise<ActivityWindow>((resolve, reject) => {
      if (util.isUndefinedOrNull(windowTypeOrConfiguration)) {
        reject("windowType is undefined");
      }

      let windowDefinition: WindowDefinition;
      if (util.isString(windowTypeOrConfiguration)) {
        windowDefinition = { type: (windowTypeOrConfiguration as string), name: "", isIndependent: false, arguments: {} };
      } else if (windowTypeOrConfiguration instanceof WindowType) {
        windowDefinition = {
          type: (windowTypeOrConfiguration as any).type || windowTypeOrConfiguration.id,
          name: windowTypeOrConfiguration.name || (windowTypeOrConfiguration as any).type || windowTypeOrConfiguration.id,
          isIndependent: false
        };
      } else {
        // G4E-3178 When creating a window in activity, the loading animation doesn't hide - extra property URL
        const invalidKeys: string[] = ["url"];

        const filteredWindowTypeOrConfiguration: any = {};

        Object.keys(windowTypeOrConfiguration).forEach((key) => {
          if (invalidKeys.indexOf(key) === -1) {
            filteredWindowTypeOrConfiguration[key] = windowTypeOrConfiguration[key];
          }
        });
        windowDefinition = filteredWindowTypeOrConfiguration as WindowDefinition;
      }

      let relativeToWindow: string | WindowSource;
      if (!util.isUndefinedOrNull(windowDefinition.relativeTo)) {
        relativeToWindow = windowDefinition.relativeTo;
        if (typeof relativeToWindow === "string") {
          const windows = this.getWindows({ type: relativeToWindow });
          if (!util.isUndefinedOrNull(windows) && windows.length > 0) {
            windowDefinition.relativeTo = windows[0].id;
          }
        } else if (!util.isUndefinedOrNull(relativeToWindow.type)) {
          const windows = this.getWindows({ type: relativeToWindow.type });
          if (!util.isUndefinedOrNull(windows) && windows.length > 0) {
            windowDefinition.relativeTo = windows[0].id;
          }
        } else if (!util.isUndefinedOrNull(relativeToWindow.windowId)) {
          windowDefinition.relativeTo = relativeToWindow.windowId;
        }
      }

      this._bridge.createWindow(activity && activity.id, windowDefinition)
        .then((wid) => {

          this._logger.debug("Window created, waiting for window entity with id " + wid);
          const handler = (window: ActivityWindow, context: EntityEventContext) => {
            // wait until the window has activity property (this means it was joined)
            if (window.id === wid && (!activity || window.activity)) {
              this._logger.debug("Got entity window with id " + wid);
              resolve(window);
              this._windows.unsubscribe(handler);
            }
          };

          // if we can not get the window right away, wait for event
          this._windows.subscribe(handler);
        })
        .catch((err) => {
          reject(err);
        });

    });
    return nodeify(promise, callback);
  }

  /**
   * Creates relative windows from a given types and joins them to an activity
   *
   * @param activity               Activity to join relative windows to
   * @param relativeWindowTypes    Relative window types to join (string or window definitions array)
   * @param callback               (Optional) Result callback
   * @returns                      Promise for ActivityWindow
   */
  public createStackedWindows(activity: Activity, relativeWindowTypes: (string | WindowDefinition)[], timeout?: number, callback?: Callback<ActivityWindow[]>): Promise<ActivityWindow[]> {
    const promise = new Promise<ActivityWindow[]>((resolve, reject) => {
      if (util.isUndefinedOrNull(activity)) {
        reject("activity is undefined");
      }
      if (util.isUndefinedOrNull(relativeWindowTypes)) {
        reject("relativeWindowTypes is undefined");
      }

      if (!Array.isArray(relativeWindowTypes)) {
        reject("relativeWindowTypes has to be an array");
      }
      if (util.isUndefinedOrNull(timeout)) {
        timeout = 20000; // default timeout: 20 seconds
      }

      const windowDefinitions: WindowDefinition[] = [];
      relativeWindowTypes.forEach((element) => {
        let windowDefinition: WindowDefinition;
        if (util.isString(element)) {
          windowDefinition = { type: (element as string), name: "", isIndependent: false, arguments: {} };
        } else {
          windowDefinition = (element as WindowDefinition);
        }

        // a hint that this comes from createStackedWindows
        windowDefinition.stackedWindow = true;
        windowDefinition.timeout = timeout;

        let relativeToWindow: WindowSource;
        if (!util.isUndefinedOrNull(windowDefinition.relativeTo)) {
          // this can not be string, converted in the prev. layer
          relativeToWindow = windowDefinition.relativeTo as WindowSource;
          if (!util.isUndefinedOrNull(relativeToWindow.type)) {
            windowDefinition.relativeTo = relativeToWindow.type;
          } else if (!util.isUndefinedOrNull(relativeToWindow.windowId)) {
            const windows = this.getWindows({ id: relativeToWindow.windowId });
            if (!util.isUndefinedOrNull(windows) && windows.length > 0) {
              windowDefinition.relativeTo = windows[0].type.name;
            }
          }
        }
        windowDefinitions.push(windowDefinition);
      });

      const tasks: Promise<ActivityWindow>[] = [];
      windowDefinitions.forEach((wd) => tasks.push(this.createWindow(activity, wd)));

      Promise.all(tasks).then(resolve).catch(reject);
    });
    return nodeify(promise, callback);
  }

  /**
   * Joins a new window from a given type to the activity
   *
   * @param activity      Activity to join the window to
   * @param window        The window to join
   * @param callback      (Optional) Result callback
   * @returns             Promise for ActivityWindow
   */
  public addWindowToActivity(activity: Activity, window: ActivityWindow, callback?: Callback<ActivityWindow>): Promise<ActivityWindow> {
    const toReturn = this._bridge.joinActivity(activity.id, window.id)
      .then(() => window);
    nodeify(toReturn, callback);
    return toReturn;
  }

  /**
   * Removes a window from the activity
   *
   * @param activity  Activity to remove the window from
   * @param           The window to remove
   * @param           (Optional) Result callback
   * @returns         Promise for ActivityWindow
   */
  public leaveWindowFromActivity(activity: Activity, window: ActivityWindow, callback?: Callback<ActivityWindow>): Promise<ActivityWindow> {
    const toReturn = this._bridge.leaveActivity(activity.id, window.id)
      .then(() => window);
    nodeify(toReturn, callback);
    return toReturn;
  }

  /**
   * Replaces the activity context
   * @param activity  Activity
   * @param context   The new context
   * @param callback  (Optional) Result callback
   * @returns         Promise for Activity
   */
  public setActivityContext(activity: Activity, context: object, callback?: Callback<Activity>): Promise<Activity> {
    const promise = new Promise<Activity>((resolve, reject) => {
      if (util.isUndefinedOrNull(activity)) {
        reject("activity can not be null");
      }

      // TODO: duplication with updateActivityContext

      // DONE: verify is the change visible here when 'updateActivityContext' resolves?
      this._bridge
        .updateActivityContext(activity, context, true)
        .then((_) => {
          resolve(activity);
        })
        .catch((err) => {
          reject(err);
        });
    });

    return nodeify(promise, callback);
  }

  /**
   * Updates activity context using the properties from  context argument.
   * If old context is {a:1, b:2, c:3} and invoking updateContext({b:3, c:null}) will result a context
   * be {a:1, b:3}
   *
   * @param activity
   * @param context
   * @param callback
   */
  public updateActivityContext(activity: Activity, context: any, callback?: Callback<Activity>): Promise<Activity> {
    const promise = new Promise<Activity>((resolve, reject) => {
      if (util.isUndefinedOrNull(activity)) {
        reject("activity can not be null");
      }

      const removedKeys = [];
      for (const key in context) {
        if (context.hasOwnProperty(key) && context[key] === null) {
          removedKeys.push(key);
        }
      }
      for (const removedKey of removedKeys) {
        delete context[removedKey];
      }

      this._bridge
        .updateActivityContext(activity, context, false, removedKeys)
        .then((_) => {
          // DONE: verify is the change already visible here when 'updateActivityContext' resolves?
          resolve(activity);
        })
        .catch((err) => {
          reject(err);
        });
    });

    return nodeify(promise, callback);
  }

  /**
   * Subscribe for activity context update events
   *
   * @param activity  Activity
   * @param handler   Handler function that will receive context related events
   * @returns {}
   */
  public subscribeActivityContextChanged(handler: ActivityContextUpdateHandler) {
    this._activities.subscribe((act, context) => {
      if (context.type === EntityEventType.ActivityContextChange) {
        const updateContext = context as ActivityContextChangedEventContext;
        handler(act, updateContext.context, updateContext.updated, updateContext.removed);
      }
    });
  }

  /**
   * Stops an activity
   * @returns {}
   */
  public stopActivity(activity: Activity, callback?: Callback<boolean>): Promise<boolean> {
    const promise = this._bridge.stopActivity(activity);
    return nodeify(promise, callback);
  }

  /**
   * Returns activity windows based on some filter. If no filter supplied all activity windows are returned.
   * @param filter  Filter that
   * @returns Array of ActivityWindows that match the filter object
   */
  public getWindows(filter: { id?: string, type?: string, name?: string, activityId?: string }): ActivityWindow[] {
    if (util.isUndefined(filter)) {
      return this._windows.get();
    }

    if (!util.isUndefined(filter.id)) {
      return [this._windows.getByName(filter.id)];
    }

    const allWindows = this._windows.get();

    return allWindows.filter((w) => {

      if (!util.isUndefined(filter.type) && w.type.id !== filter.type) {
        return false;
      }

      if (!util.isUndefined(filter.name) && w.name !== filter.name) {
        return false;
      }

      if (!util.isUndefined(filter.activityId)) {
        if (util.isUndefinedOrNull(w.activity)) {
          return false;
        }

        if (w.activity.id !== filter.activityId) {
          return false;
        }
      }

      return true;
    });
  }

  public getWindowBounds(id: string): Promise<WindowBounds> {
    return this._bridge.getWindowBounds(id);
  }

  public setWindowBounds(id: string, bounds: WindowBounds, callback?: Callback<ActivityWindow>): Promise<any> {
    const promise = new Promise<void>((resolve, reject) => {
      this._bridge.setWindowBounds(id, bounds)
        .then(() => resolve())
        .catch((err) => reject(err));
    });

    return nodeify(promise, callback);
  }

  public closeWindow(id: string): Promise<any> {
    return this._bridge.closeWindow(id);
  }

  public activateWindow(id: string, focus: boolean): Promise<any> {
    return this._bridge.activateWindow(id, focus);
  }

  public setWindowVisibility(id: string, visible: boolean): Promise<any> {
    return this._bridge.setWindowVisibility(id, visible);
  }

  public clone(activity: Activity, cloneOptions: CloneOptions, callback?: Callback<Activity>) {
    const promise = new Promise<any>((resolve, reject) => {
      if (!activity) {
        reject("activity can not be null");
      }

      this._bridge.cloneActivity(activity.id, cloneOptions)
        .then((activityId) => {
          this._activities
            .getOrWait(activityId)
            .then((act: Activity) => {
              resolve(act);
            })
            .catch((err) => reject(err));
        })
        .catch((err) => reject(err));
    });

    return nodeify(promise, callback);
  }

  public attachActivities(from: string, to: string, tag?: object, callback?: Callback<AttachedActivityDescriptor>): Promise<AttachedActivityDescriptor> {

    tag = tag || {};

    const promise = new Promise<AttachedActivityDescriptor>((resolve, reject) => {

      const fromActivity = this._activities.getByName(from);
      if (!fromActivity) {
        reject("can not find activity with id " + from);
        return;
      }

      const toActivity = this._activities.getByName(to);
      if (!toActivity) {
        reject("can not find activity with id " + to);
        return;
      }

      return this._bridge.attachActivities(from, to, tag)
        .then((data: any) => {
          const newActId = data.to;
          const state = data.descriptor;
          const allStates = data.descriptors;

          this._activities.getOrWait(newActId).then((act) => {
            act._updateDescriptors(allStates);
            const stateWrapped = act.attached.filter((u) => u.ownerId === state.ownerId)[0];

            resolve(stateWrapped);
          });
        })
        .catch((err) => {
          reject(err);
        });
    });

    return nodeify(promise, callback);
  }

  public detachActivities(
    activityId: string,
    descriptor: ActivityDescriptor,
    callback?: Callback<Activity>): Promise<Activity> {
    const promise = new Promise<Activity>((resolve, reject) => {
      return this._bridge.detachActivities(activityId, descriptor)
        .then(/* (params)*/() => {
          // #deleteme TODO: deal with this
          const oldActId: string = undefined; // params.oldActivityId;
          const newActId: string = undefined; // params.newActivityId;
          const descriptors: any = undefined; // params.descriptors;

          this._activities
            .getOrWait(oldActId)
            .then((oldAct: Activity) => {
              oldAct._updateDescriptors(descriptors);

              this._activities
                .getOrWait(newActId)
                .then((newAct: Activity) => {
                  resolve(newAct);
                });
            })
            .catch((err) => reject(err));
        })
        .catch((err) => {
          reject(err);
        });
    });

    return nodeify(promise, callback);
  }

  public subscribeActivitiesAttached(callback: any) {
    this._attachedCallbacks.push(callback);
  }

  public subscribeActivitiesDetached(callback: any) {
    this._detachedCallbacks.push(callback);
  }

  public subscribeActivityFrameColorChanged(callback: () => void) {
    this._frameColorChangesCallbacks.push(callback);
  }

  /** Assigns a manager to a given ActivityEntity */
  private _grabEntity(entity: ActivityEntity) {
    entity._manager = this;
  }

  private _getInitialData() {
    this._logger.debug("Request initial data...");

    // NB: there's no possibility for duplicate entities from events that have come
    // in between _bridge.init() and _bridge.ready()'s resolve, since the ObservableCollections
    // coalesce items by id.

    this._bridge.getActivityTypes()
      .then((at) => {
        this._activityTypes.add(at);
        this._dataReadyMarker.signal("Got act types");
      })
      .catch((error) => {
        this._logger.error(error);
        this._dataReadyMarker.error("Can not initialize ActivityManager - error getting activity types -" + error);
      });

    this._bridge.getWindowTypes()
      .then((wt) => {
        this._windowTypes.add(wt);
        this._dataReadyMarker.signal("Got window types");
      })
      .catch((error) => {
        this._logger.error(error);
        this._dataReadyMarker.error("Can not initialize ActivityManager - error getting window types  " + error);
      });

    this._bridge.getActivities()
      .then((ac) => {
        this._activities.add(ac);
        this._dataReadyMarker.signal("Got activities");
      })
      .catch((error) => {
        this._logger.error(error);
        this._dataReadyMarker.error("Can not initialize ActivityManager - error getting activity instances -" + error);
      });

    this._bridge.getActivityWindows()
      .then((aw) => {
        this._windows.add(aw);
        this._dataReadyMarker.signal("Got windows");
      })
      .catch((error) => {
        this._logger.error(error);
        this._dataReadyMarker.error("Can not initialize ActivityManager - error getting activity windows -" + error);
      });
  }

  private _subscribeForData() {
    this._logger.debug("Subscribe for data...");

    // NB: there's no possibility for duplicate entities from events that have come
    // in between _bridge.init() and _bridge.ready()'s resolve, since the ObservableCollections
    // coalesce items by id.

    this._bridge.onActivityTypeStatusChange((event) => {
      this._activityTypes.process(event);
    });

    this._bridge.onWindowTypeStatusChange((event) => {
      this._windowTypes.process(event);
    });

    this._bridge.onActivityWindowChange((event) => {
      this._windows.process(event);
    });

    this._bridge.onActivityStatusChange((event: EntityEvent<Activity>) => {
      this._activities.process(event);
    });
  }

  private _handleActivitiesAttached(data: any) {

    const newActId = data.to;
    const descriptor = data.descriptor;
    const descriptors = data.descriptors;

    this._activities.getOrWait(newActId).then((act) => {
      act._updateDescriptors(descriptors);
      const descriptorAsObjectFromAPI = act.attached.filter((u) => u.ownerId === descriptor.ownerId)[0];

      this._attachedCallbacks.forEach((callback) => {
        try {
          callback(act, descriptorAsObjectFromAPI);
        } catch (err) {
          return;
        }
      });

    });
  }

  private _handleActivitiesDetached(data: any) {
    const oldActId = data.oldActivityId;
    const newActId = data.newActivityId;
    const descriptors = data.descriptors;
    const descriptor = data.descriptor;

    this._activities.getOrWait(oldActId).then((oldAct) => {

      oldAct._updateDescriptors(descriptors);

      this._activities.getOrWait(newActId).then((newAct) => {

        this._detachedCallbacks.forEach((callback) => {
          try {
            callback(newAct, oldAct, descriptor);
          } catch (err) {
            return;
          }
        });
      });
    });
  }

  private _handleActivityDescriptorsRefreshed(data: any) {
    const id = data.id;
    const descriptors = data.descriptors;

    const act = this._activities.getByName(id);
    if (act) {
      act._updateDescriptors(descriptors);
    }
  }

  private refreshDescriptors() {
    this._bridge.getAttachedDescriptors()
      .then((map: any) => {

        // populate descriptors
        if (map) {
          Object.keys(map).forEach((key: string) => {
            const actId = key;
            const descriptors = map[key];

            const act = this._activities.getByName(actId);
            if (act) {
              act._updateDescriptors(descriptors);
            }
          });
        }

        this._descriptorsMarker.signal("Successfully got descriptors");
      })
      .catch((err) => {
        this._descriptorsMarker.error("failed to get descriptors - " + err);
      });
  }

  private _handleWindowFrameColorChanged(win: Glue42.Windows.GDWindow) {
    if (!win.activityId) {
      return;
    }

    const act = this._activities.getByName(win.activityId);
    if (!act) {
      return;
    }

    // notify for the owner only
    if (!act.owner) {
      return;
    }

    if (act.owner.underlyingWindow.id !== win.id) {
      return;
    }

    this._frameColorChangesCallbacks.forEach((callback) => {
      try {
        callback(act, win.frameColor);
      } catch (e) {
        return;
      }
    });
  }
}
